import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider';
import DriveProvider, { useDrive } from './contexts/DriveProvider';
import PrivateRoute from './components/PrivateRoute';
import AppShell from './components/drive/AppShell';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import FolderPage from './pages/FolderPage';
import CollectionPage from './pages/CollectionPage';
import LinksPage from './pages/LinksPage';
import PeoplePage from './pages/PeoplePage';
import UsagePage from './pages/UsagePage';
import GuestPortal from './pages/GuestPortal';
import DesktopLinkPage from './pages/DesktopLinkPage';
import DesktopPage from './pages/DesktopPage';

/** "/" opens My Drive once the drive list has loaded. */
const Home = () => {
  const { personalDrive, drivesLoaded } = useDrive();
  if (personalDrive) return <Navigate to={`/drive/${personalDrive.id}`} replace />;
  if (drivesLoaded) {
    return <p className="p-10 text-center text-muted-foreground">Could not load your drives. Refresh to try again.</p>;
  }
  return null;
};

function App() {
  // Apply saved theme on app load (before any page renders)
  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem('theme'); } catch { /* storage blocked */ }
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    if (stored === 'dark' || (!stored && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso-callback" element={<SSOCallback />} />
          {/* Public guest portal: no LampTwist sign-in */}
          <Route path="/s/:token" element={<GuestPortal />} />
          <Route path="/desktop/link" element={<PrivateRoute><DesktopLinkPage /></PrivateRoute>} />
          <Route
            element={
              <PrivateRoute>
                <DriveProvider>
                  <AppShell />
                </DriveProvider>
              </PrivateRoute>
            }
          >
            <Route path="/" element={<Home />} />
            <Route path="/drive/:driveId" element={<FolderPage />} />
            <Route path="/drive/:driveId/folder/:folderId" element={<FolderPage />} />
            <Route path="/shared" element={<CollectionPage key="shared" source="shared" />} />
            <Route path="/recent" element={<CollectionPage key="recent" source="recent" />} />
            <Route path="/starred" element={<CollectionPage key="starred" source="starred" />} />
            <Route path="/trash" element={<CollectionPage key="trash" source="trash" />} />
            <Route path="/search" element={<CollectionPage key="search" source="search" />} />
            <Route path="/links" element={<LinksPage />} />
            <Route path="/admin/people" element={<PeoplePage />} />
            <Route path="/admin/usage" element={<UsagePage />} />
            <Route path="/desktop" element={<DesktopPage />} />
            <Route path="/devices" element={<Navigate to="/desktop" replace />} />
          </Route>
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
